<div class="space-y-5">

  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h2 class="text-2xl font-bold text-gray-800">Subject Offerings</h2>
      <p class="text-sm text-gray-500 mt-1">
        {{ filteredOfferings.length }} offering{{ filteredOfferings.length === 1 ? '' : 's' }} found
      </p>
    </div>
    <button
      (click)="openAddModal()"
      class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
      </svg>
      Add Offering
    </button>
  </div>

  <!-- Search -->
  <div class="relative">
    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
    </svg>
    <input
      type="text"
      [(ngModel)]="searchQuery"
      (ngModelChange)="onSearch()"
      placeholder="Search by subject, form, or year..."
      class="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"
    />
  </div>

  <!-- Loader -->
  <app-loader-component [loading]="loading" message="Loading offerings..."></app-loader-component>

  <!-- Error -->
  <div *ngIf="errorMsg && !loading" class="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
    <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
    </svg>
    {{ errorMsg }}
  </div>

  <!-- Empty -->
  <div *ngIf="!loading && !errorMsg && filteredOfferings.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
    <svg class="w-10 h-10 text-gray-400 mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15"/>
    </svg>
    <p class="text-sm font-medium text-gray-600">No offerings found</p>
    <p class="text-xs text-gray-400 mt-1">Try adjusting your search or add a new offering.</p>
  </div>

  <!-- Table -->
  <div *ngIf="!loading && pagedOfferings.length > 0" class="rounded-xl border border-gray-200 overflow-hidden">
    <table class="w-full text-sm">
      <thead>
        <tr class="bg-gray-50 border-b border-gray-200">
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 w-8">#</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Subject</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 hidden sm:table-cell">Form</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 hidden md:table-cell">Year</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 hidden lg:table-cell">Teacher</th>
          <th class="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Actions</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
        <tr
          *ngFor="let offering of pagedOfferings; let i = index"
          class="bg-white hover:bg-gray-50 transition-colors cursor-pointer"
          (click)="openDetailModal(offering)"
        >
          <td class="px-4 py-3 text-gray-400 text-xs">{{ page * size + i + 1 }}</td>

          <!-- Subject -->
          <td class="px-4 py-3">
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                [style.background]="(getSubjectById(offering.subjectId)?.themeColor || '#6366f1') + '22'"
              >
                <i
                  [class]="'bi bi-' + (getSubjectById(offering.subjectId)?.icon || 'book') + ' text-sm'"
                  [style.color]="getSubjectById(offering.subjectId)?.themeColor || '#6366f1'"
                ></i>
              </div>
              <span class="font-semibold text-gray-800">{{ offering.subjectName || getSubjectById(offering.subjectId)?.name || '—' }}</span>
            </div>
          </td>

          <!-- Form -->
          <td class="px-4 py-3 hidden sm:table-cell">
            <span class="inline-block px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-xs font-semibold text-gray-600">
              {{ offering.formName || '—' }}
            </span>
          </td>

          <!-- Year -->
          <td class="px-4 py-3 hidden md:table-cell">
            <span class="text-sm text-gray-600">{{ offering.academicYearName || '—' }}</span>
          </td>

          <!-- Teacher -->
          <td class="px-4 py-3 hidden lg:table-cell">
            <span *ngIf="offering.assignedTeacherId" class="flex items-center gap-1.5 text-sm text-gray-600">
              <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
              </svg>
              {{ getTeacherById(offering.assignedTeacherId)?.fullName || 'Teacher #' + offering.assignedTeacherId }}
            </span>
            <span *ngIf="!offering.assignedTeacherId" class="text-xs text-gray-400 italic">Unassigned</span>
          </td>

          <!-- Actions -->
          <td class="px-4 py-3" (click)="$event.stopPropagation()">
            <div class="flex items-center justify-end gap-1">

              <!-- View topics -->
              <button
                (click)="openDetailModal(offering)"
                title="View topics"
                class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary cursor-pointer transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178Z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                </svg>
              </button>

              <!-- Assign teacher -->
              <button
                (click)="openAssignModal(offering)"
                title="Assign teacher"
                class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-amber-600 cursor-pointer transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"/>
                </svg>
              </button>

              <!-- Edit -->
              <button
                (click)="openEditModal(offering)"
                title="Edit offering"
                class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary cursor-pointer transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487 18.549 2.8a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"/>
                </svg>
              </button>

              <!-- Delete -->
              <button
                (click)="openDeleteModal(offering)"
                title="Delete offering"
                class="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 cursor-pointer transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                </svg>
              </button>

            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <div *ngIf="filteredOfferings.length > size" class="flex items-center justify-between pt-2">
    <span class="text-xs text-gray-400">
      Showing {{ getMinValue(page * size + size, filteredOfferings.length) }} of {{ filteredOfferings.length }}
    </span>
    <div class="flex items-center gap-1">
      <button
        (click)="prevPage()"
        [disabled]="!hasPrev"
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
        </svg>
        Prev
      </button>
      <span class="px-3 py-1.5 text-sm text-gray-600">{{ page + 1 }} / {{ totalPages }}</span>
      <button
        (click)="nextPage()"
        [disabled]="!hasNext"
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Next
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>
  </div>

</div>


<!-- ══════════════════════════════════════ ADD OFFERING MODAL -->
<div *ngIf="showAddModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeAddModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Add Offering</h3>
      <button (click)="closeAddModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <div *ngIf="addError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ addError }}
    </div>

    <div class="space-y-4">
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Subject *</label>
        <select
          [(ngModel)]="addForm.subjectId"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
        >
          <option value="">Select subject...</option>
          <option *ngFor="let s of allSubjects" [value]="s.id">{{ s.name }}</option>
        </select>
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Form *</label>
        <select
          [(ngModel)]="addForm.formId"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
        >
          <option value="">Select form...</option>
          <option *ngFor="let f of allForms" [value]="f.id">{{ f.name }}</option>
        </select>
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Academic Year *</label>
        <select
          [(ngModel)]="addForm.academicYearId"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
        >
          <option value="">Select academic year...</option>
          <option *ngFor="let ay of allAcademicYears" [value]="ay.id">{{ ay.name }}</option>
        </select>
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Teacher <span class="normal-case text-gray-400 font-normal">(optional)</span></label>
        <select
          [(ngModel)]="addForm.assignedTeacherId"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
        >
          <option value="">No teacher assigned</option>
         <option *ngFor="let t of allTeachers" [value]="t.id">
  {{ t.fullName }}
</option>
        </select>
      </div>
      
    </div>

    <div class="flex items-center justify-end gap-3 pt-1">
      <button (click)="closeAddModal()" [disabled]="addLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="submitAdd()" [disabled]="addLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="addLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ addLoading ? 'Saving...' : 'Add Offering' }}
      </button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ EDIT OFFERING MODAL -->
<div *ngIf="showEditModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeEditModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Edit Offering</h3>
      <button (click)="closeEditModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <div *ngIf="editError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ editError }}
    </div>

    <div class="space-y-4">
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Subject *</label>
        <select [(ngModel)]="editForm.subjectId" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors">
          <option value="">Select subject...</option>
          <option *ngFor="let s of allSubjects" [value]="s.id">{{ s.name }}</option>
        </select>
      </div>
      <div class="space-y-1.5">
  <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Form *</label>
  <select
    [(ngModel)]="editForm.formId"
    class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
  >
    <option value="">Select form...</option>
    <option *ngFor="let f of allForms" [value]="f.id">
      {{ f.name }}
    </option>
  </select>
</div>

<div class="space-y-1.5">
  <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Academic Year *</label>
  <select
    [(ngModel)]="editForm.academicYearId"
    class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
  >
    <option value="">Select academic year...</option>
    <option *ngFor="let ay of allAcademicYears" [value]="ay.id">
      {{ ay.name }}
    </option>
  </select>
</div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Teacher <span class="normal-case text-gray-400 font-normal">(optional)</span></label>
        <select [(ngModel)]="editForm.assignedTeacherId" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors">
          <option value="">No teacher assigned</option>
          <option *ngFor="let t of allTeachers" [value]="t.id">{{ t.fullName }}</option>
        </select>
      </div>
    </div>

    <div class="flex items-center justify-end gap-3 pt-1">
      <button (click)="closeEditModal()" [disabled]="editLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="submitEdit()" [disabled]="editLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="editLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ editLoading ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ DELETE OFFERING MODAL -->
<div *ngIf="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeDeleteModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">
    <div class="flex items-start gap-4">
      <div class="w-10 h-10 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
        <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
        </svg>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-800">Delete Offering</h3>
        <p class="text-sm text-gray-600 mt-1">Are you sure you want to delete the offering for <span class="font-semibold">{{ selectedOffering?.subjectName || 'this subject' }}</span>? This action cannot be undone.</p>
      </div>
    </div>

    <div *ngIf="deleteError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ deleteError }}
    </div>

    <div class="flex items-center justify-end gap-3">
      <button (click)="closeDeleteModal()" [disabled]="deleteLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="confirmDelete()" [disabled]="deleteLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="deleteLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ deleteLoading ? 'Deleting...' : 'Delete' }}
      </button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ ASSIGN TEACHER MODAL -->
<div *ngIf="showAssignModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeAssignModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Assign Teacher</h3>
      <button (click)="closeAssignModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <p class="text-sm text-gray-600">Assigning to <span class="font-semibold">{{ selectedOffering?.subjectName || 'this offering' }}</span> — {{ selectedOffering?.grade }}, {{ selectedOffering?.academicYear }}</p>

    <div *ngIf="assignError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ assignError }}
    </div>

    <div class="space-y-1.5">
      <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Teacher *</label>
      <select [(ngModel)]="assignForm.assignedTeacherId" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors">
        <option value="">Select a teacher...</option>
        <option *ngFor="let t of allTeachers" [value]="t.id">{{ t.fullName }}</option>
      </select>
    </div>

    <div class="flex items-center justify-end gap-3 pt-1">
      <button (click)="closeAssignModal()" [disabled]="assignLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="submitAssign()" [disabled]="assignLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="assignLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ assignLoading ? 'Assigning...' : 'Assign Teacher' }}
      </button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ DETAIL / TOPICS MODAL -->
<div *ngIf="showDetailModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeDetailModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col max-h-[90vh]" (click)="$event.stopPropagation()">

    <!-- Modal header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          [style.background]="(getSubjectById(selectedOffering?.subjectId)?.themeColor || '#6366f1') + '22'"
        >
          <i
            [class]="'bi bi-' + (getSubjectById(selectedOffering?.subjectId)?.icon || 'book') + ' text-lg'"
            [style.color]="getSubjectById(selectedOffering?.subjectId)?.themeColor || '#6366f1'"
          ></i>
        </div>
        <div>
          <h3 class="text-base font-bold text-gray-800">{{ selectedOffering?.subjectName || getSubjectById(selectedOffering?.subjectId)?.name }}</h3>
          <p class="text-xs text-gray-400">{{ selectedOffering?.grade }} &middot; {{ selectedOffering?.academicYear }}</p>
        </div>
      </div>
      <button (click)="closeDetailModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <!-- Info strip -->
    <div class="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-6 flex-shrink-0">
      <div class="flex items-center gap-2 text-sm text-gray-600">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
        </svg>
        <span *ngIf="selectedOffering?.assignedTeacherId">{{ getTeacherById(selectedOffering?.assignedTeacherId)?.fullName || 'Teacher #' + selectedOffering?.assignedTeacherId }}</span>
        <span *ngIf="!selectedOffering?.assignedTeacherId" class="italic text-gray-400">No teacher assigned</span>
      </div>
      <div class="flex items-center gap-2 text-sm text-gray-600">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"/>
        </svg>
        {{ topics.length }} topic{{ topics.length === 1 ? '' : 's' }}
      </div>
    </div>

    <!-- Topics section -->
    <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
      <div class="flex items-center justify-between">
        <h4 class="text-sm font-semibold text-gray-700">Topics</h4>
        <button
          (click)="openTopicAddModal()"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold cursor-pointer hover:opacity-90 transition-opacity"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/></svg>
          Add Topic
        </button>
      </div>

      <!-- Topics loader -->
      <app-loader-component [loading]="topicsLoading" message="Loading topics..."></app-loader-component>

      <!-- Topics error -->
      <div *ngIf="topicsError && !topicsLoading" class="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
        <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
        {{ topicsError }}
      </div>

      <!-- No topics -->
      <div *ngIf="!topicsLoading && !topicsError && topics.length === 0" class="flex flex-col items-center justify-center py-10 text-center">
        <svg class="w-8 h-8 text-gray-300 mb-2" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"/>
        </svg>
        <p class="text-sm text-gray-400">No topics yet — add one to get started.</p>
      </div>

      <!-- Topics list -->
      <div *ngIf="!topicsLoading && topics.length > 0" class="space-y-2">
        <div
          *ngFor="let topic of topics; let i = index"
          class="flex items-start justify-between gap-3 p-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
        >
          <div class="flex items-start gap-3 min-w-0">
            <div class="w-7 h-7 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span class="text-xs font-bold text-gray-400">{{ i + 1 }}</span>
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-gray-800 truncate">{{ topic.title }}</p>
              <p *ngIf="topic.description" class="text-xs text-gray-400 mt-0.5 line-clamp-2">{{ topic.description }}</p>
            </div>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <button (click)="openTopicEditModal(topic)" title="Edit topic" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-primary cursor-pointer transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487 18.549 2.8a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"/></svg>
            </button>
            <button (click)="openTopicDeleteModal(topic)" title="Delete topic" class="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 cursor-pointer transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal footer -->
    <div class="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
      <button (click)="closeDetailModal()" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors">Close</button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ ADD TOPIC MODAL -->
<div *ngIf="showTopicAddModal" class="fixed inset-0 z-[60] flex items-center justify-center p-4" (click)="closeTopicAddModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Add Topic</h3>
      <button (click)="closeTopicAddModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <div *ngIf="topicAddError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ topicAddError }}
    </div>

    <div class="space-y-4">
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Title *</label>
        <input type="text" [(ngModel)]="topicAddForm.title" placeholder="e.g. Algebra Basics" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"/>
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Description <span class="normal-case text-gray-400 font-normal">(optional)</span></label>
        <textarea [(ngModel)]="topicAddForm.description" rows="3" placeholder="Brief description of the topic..." class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors resize-none"></textarea>
      </div>
    </div>

    <div class="flex items-center justify-end gap-3 pt-1">
      <button (click)="closeTopicAddModal()" [disabled]="topicAddLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="submitTopicAdd()" [disabled]="topicAddLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="topicAddLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ topicAddLoading ? 'Saving...' : 'Add Topic' }}
      </button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ EDIT TOPIC MODAL -->
<div *ngIf="showTopicEditModal" class="fixed inset-0 z-[60] flex items-center justify-center p-4" (click)="closeTopicEditModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Edit Topic</h3>
      <button (click)="closeTopicEditModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <div *ngIf="topicEditError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ topicEditError }}
    </div>

    <div class="space-y-4">
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Title *</label>
        <input type="text" [(ngModel)]="topicEditForm.title" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"/>
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Description <span class="normal-case text-gray-400 font-normal">(optional)</span></label>
        <textarea [(ngModel)]="topicEditForm.description" rows="3" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors resize-none"></textarea>
      </div>
    </div>

    <div class="flex items-center justify-end gap-3 pt-1">
      <button (click)="closeTopicEditModal()" [disabled]="topicEditLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="submitTopicEdit()" [disabled]="topicEditLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="topicEditLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ topicEditLoading ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ DELETE TOPIC MODAL -->
<div *ngIf="showTopicDeleteModal" class="fixed inset-0 z-[60] flex items-center justify-center p-4" (click)="closeTopicDeleteModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">
    <div class="flex items-start gap-4">
      <div class="w-10 h-10 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
        <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-800">Delete Topic</h3>
        <p class="text-sm text-gray-600 mt-1">Are you sure you want to delete <span class="font-semibold">{{ selectedTopic?.title }}</span>? This action cannot be undone.</p>
      </div>
    </div>

    <div *ngIf="topicDeleteError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ topicDeleteError }}
    </div>

    <div class="flex items-center justify-end gap-3">
      <button (click)="closeTopicDeleteModal()" [disabled]="topicDeleteLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="confirmTopicDelete()" [disabled]="topicDeleteLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="topicDeleteLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        {{ topicDeleteLoading ? 'Deleting...' : 'Delete' }}
      </button>
    </div>
  </div>
</div>